import type { Node } from '@xyflow/react';
import { NODE_TYPES } from '../types';
import type { BaseNodeData } from '../types';
import type { ChainTerminus } from '../hooks/useChainTermini';
import type { BehaviorCaseNodeData, ParentCaseInfo, SegmentNodeData } from '../types/stepNodes';

function findBehaviorCaseNode(
	nodes: Node<BaseNodeData>[],
	parentSwitchNodeId: string,
	caseId?: string,
	caseIndex?: number
): BehaviorCaseNodeData | undefined {
	const behaviorNode = nodes.find(node => {
		if (node.type !== NODE_TYPES.BEHAVIOR_CASE) {
			return false;
		}
		const data = node.data as BehaviorCaseNodeData;
		if (data.parentSwitchNodeId !== parentSwitchNodeId) {
			return false;
		}
		return caseId ? data.caseId === caseId : data.caseIndex === caseIndex;
	});

	return behaviorNode?.data as BehaviorCaseNodeData | undefined;
}

/**
 * Resolves parent case metadata for a newly inserted step.
 *
 * This supports:
 * - direct parentCaseInfo already on the terminus node
 * - branch-only metadata from ChainTerminus (before refresh)
 * - hydration of missing behavior metadata (e.g. sourceStepName) for nested inserts
 */
export function deriveParentCaseInfoForNewNode(
	terminus: ChainTerminus,
	nodes: Node<BaseNodeData>[]
): ParentCaseInfo | undefined {
	const directParentCaseInfo = (terminus.node.data as { parentCaseInfo?: ParentCaseInfo })?.parentCaseInfo;

	if (!terminus.branchInfo) {
		if (!directParentCaseInfo) {
			return undefined;
		}

		// Nested behavior inserts rely on this for schedule language like
		// "after the reply to New Email".
		if (directParentCaseInfo.isBehaviorCase && !directParentCaseInfo.sourceStepName) {
			const hydratedBehavior = findBehaviorCaseNode(
				nodes,
				directParentCaseInfo.parentSwitchNodeId || '',
				directParentCaseInfo.caseId,
				directParentCaseInfo.caseIndex
			);
			if (hydratedBehavior?.sourceStepName) {
				return {
					...directParentCaseInfo,
					sourceStepName: hydratedBehavior.sourceStepName,
				};
			}
		}

		return directParentCaseInfo;
	}

	const { parentSwitchId, caseIndex, caseName, caseId } = terminus.branchInfo;
	const parentSwitchNode = nodes.find(node => node.id === parentSwitchId);
	const parentSwitchType = (
		(
			parentSwitchNode?.data as {
				automationStep?: {
					_type?: string;
				};
			}
		)?.automationStep?._type || ''
	).toString();
	const isBehaviorCase = parentSwitchType === 'BehaviorSwitchAutomationStep';
	const defaultColor = '#3B82F6';

	if (isBehaviorCase) {
		const behaviorData = findBehaviorCaseNode(nodes, parentSwitchId, caseId, caseIndex);
		const resolvedFromBranch: ParentCaseInfo = {
			caseIndex,
			caseName: behaviorData?.caseName || caseName,
			isDefault: behaviorData?.isDefault === true,
			caseColor: behaviorData?.caseColor || defaultColor,
			parentSwitchNodeId: parentSwitchId,
			caseId: behaviorData?.caseId || caseId,
			globalSegmentOrdinal: behaviorData?.globalSegmentOrdinal,
			segmentPathKey: behaviorData?.segmentPathKey,
			isBehaviorCase: true,
			sourceStepName: behaviorData?.sourceStepName,
		};

		if (!directParentCaseInfo) {
			return resolvedFromBranch;
		}

		if (directParentCaseInfo.isBehaviorCase) {
			const sameSwitch = directParentCaseInfo.parentSwitchNodeId === resolvedFromBranch.parentSwitchNodeId;
			const sameCaseById =
				directParentCaseInfo.caseId &&
				resolvedFromBranch.caseId &&
				directParentCaseInfo.caseId === resolvedFromBranch.caseId;
			const sameCaseByIndex = directParentCaseInfo.caseIndex === resolvedFromBranch.caseIndex;

			if (!sameSwitch || (!sameCaseById && !sameCaseByIndex)) {
				return resolvedFromBranch;
			}

			if (!resolvedFromBranch.sourceStepName && directParentCaseInfo.sourceStepName) {
				return {
					...resolvedFromBranch,
					sourceStepName: directParentCaseInfo.sourceStepName,
				};
			}
		}

		return resolvedFromBranch;
	}

	const segmentNode = nodes.find(node => {
		if (node.type !== NODE_TYPES.SEGMENT) {
			return false;
		}
		const data = node.data as SegmentNodeData;
		if (data.parentSwitchNodeId !== parentSwitchId) {
			return false;
		}
		return caseId ? data.caseId === caseId : data.segmentIndex === caseIndex;
	});

	const segmentData = segmentNode?.data as SegmentNodeData | undefined;
	const resolvedSegment: ParentCaseInfo = {
		caseIndex,
		caseName,
		isDefault: segmentData?.isDefault === true,
		caseColor: segmentData?.caseColor || defaultColor,
		parentSwitchNodeId: parentSwitchId,
		caseId,
		globalSegmentOrdinal: segmentData?.globalSegmentOrdinal,
		segmentPathKey: segmentData?.segmentPathKey,
		isBehaviorCase: false,
	};

	if (!directParentCaseInfo) {
		return resolvedSegment;
	}

	return {
		...directParentCaseInfo,
		...resolvedSegment,
	};
}
